import { useCallback, useEffect, useRef, useState } from 'react';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import Feature from 'ol/Feature';
import { Point } from 'ol/geom';
import { fromExtent } from 'ol/geom/Polygon';
import Draw, { createBox } from 'ol/interaction/Draw';
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import { Circle as CircleStyle, Fill, Stroke, Style, Text } from 'ol/style';
import { boundingExtent, getCenter } from 'ol/extent';

import { DEFAULT_BACKBONE } from '@utils/api/similarity';
import { OBJECT_COLOR_PALETTE } from '@utils/constants';
import {
	DEFAULT_PATCH_M,
	exampleBox,
	metresPerMapUnit,
	roundedBox,
} from '@utils/map/similarity/similarityBox';
import { createSimilarityHover } from '@utils/map/similarity/similarityHover';
import {
	SIMILARITY_Z_INDEX,
	createSimilarityLayer,
	similarityService,
} from '@utils/map/similarity/similaritySource';
import {
	DEFAULT_OPACITY,
	defaultThreshold,
	similarityToBand,
} from '@utils/map/similarity/similarityStyle';
import { parseSuggestions } from '@utils/map/similarity/similaritySuggestions';

export const SIMILARITY_STATUS = Object.freeze({
	IDLE: 'idle',
	MARKING: 'marking',
	LOADING: 'loading',
	READY: 'ready',
	// The orthophoto's similarity features are not computed (yet)
	UNAVAILABLE: 'unavailable',
});

const NO_SUGGESTIONS = Object.freeze([]);
const NO_BACKBONES = Object.freeze([]);
// The backbone to use when the project has its features (F01e); DEFAULT_BACKBONE otherwise
const PREFERRED_BACKBONE = 'dinov3-sat';
// Wait for the map to settle before asking for the view's suggestions
const SUGGESTION_VIEW_DEBOUNCE_MS = 400;

// Objects in one search, each searched with its own examples (one SAM 3 call a square each)
export const MAX_OBJECTS = 4;
// Each object's colour, for its examples' name tag and its results. From the object palette, without
// green (examples, ticked squares) and the orange of the score ramp.
export const OBJECT_COLORS = [
	OBJECT_COLOR_PALETTE[2],
	OBJECT_COLOR_PALETTE[6],
	OBJECT_COLOR_PALETTE[0],
	OBJECT_COLOR_PALETTE[8],
];
/** "car", or "Object 2" before it has a name */
export const objectName = (groups, index) => groups[index]?.name?.trim() || `Object ${index + 1}`;

// The example: a rounded green box with a dark halo, so it stands apart from the boxes Find draws,
// and a ring at its centre so a one-patch example stays visible. With several objects, the object's
// name sits above it in the object's colour.
const exampleStyle = (token, groupsRef) => (feature, resolution) => {
	const extent = feature.getGeometry().getExtent();
	const centre = new Point(getCenter(extent));
	const box = roundedBox(extent, token.borderRadiusLG * resolution);
	const outline = (color, width) =>
		new Style({ geometry: box, stroke: new Stroke({ color, width }) });
	const ring = (color, width) =>
		new Style({
			geometry: centre,
			image: new CircleStyle({ radius: 7, stroke: new Stroke({ color, width }) }),
		});
	const group = feature.get('group');
	const groups = groupsRef?.current ?? [];
	const tag =
		group !== undefined && groups.length > 1
			? [
					new Style({
						geometry: new Point([(extent[0] + extent[2]) / 2, extent[3]]),
						text: new Text({
							text: objectName(groups, group),
							font: `${token.fontWeightStrong} ${token.fontSizeSM}px ${token.fontFamily}`,
							textBaseline: 'bottom',
							offsetY: -token.sizeXS,
							fill: new Fill({ color: OBJECT_COLORS[group % OBJECT_COLORS.length] }),
							stroke: new Stroke({ color: token.colorBgMask, width: 3 }),
						}),
					}),
				]
			: [];
	return [
		outline(token.colorBgMask, 5),
		outline(token.colorSuccess, 3),
		ring(token.colorBgMask, 4),
		ring(token.colorSuccess, 2),
		...tag,
	];
};

// F01g: the heatmap's spots that are on an object of the kind 4 times in 5 (cars, DINOv3 SAT-L),
// as a share of the way from the median to the strongest similarity. They mark places, not boxes:
// at this cut a box of the example's size was the right one only 2 times in 3.
export const SPOT_CUTOFF = 0.85;
const spotSimilarity = ({ lo, hi }) => lo + SPOT_CUTOFF * (hi - lo);

// A spot: a small dot with a dark rim, larger when selected
const spotStyle = (token, selected) =>
	new Style({
		image: new CircleStyle({
			radius: selected ? 6 : 4,
			fill: new Fill({ color: token.colorWarning }),
			stroke: new Stroke({ color: token.colorBgMask, width: 2 }),
		}),
	});

// Objects up to this size (longest side, metres) are compared with BFM v1, larger ones with
// DINOv3 SAT-L: BFM v1 is better for 1–2-patch objects such as seedlings (F01c), SAT-L for cars (F01e)
const SMALL_OBJECT_M = 1;
const backboneFor = (boxes, ready) => {
	const longest = Math.max(
		...boxes.map(
			([minX, minY, maxX, maxY]) =>
				Math.max(maxX - minX, maxY - minY) *
				metresPerMapUnit([(minX + maxX) / 2, (minY + maxY) / 2])
		)
	);
	const [first, second] =
		longest > SMALL_OBJECT_M
			? [PREFERRED_BACKBONE, DEFAULT_BACKBONE]
			: [DEFAULT_BACKBONE, PREFERRED_BACKBONE];
	return ready.includes(first) || !ready.includes(second) ? first : second;
};

/**
 * State and map layers for "Find similar": the example, the similarity overlay and the
 * suggestions.
 *
 * @param {Object} params
 * @param {Object|null} params.mapObject - OpenLayers map
 * @param {string} [params.projectUuid]
 * @param {{min: number, max: number}} params.zoomRange - The orthophoto's tile zoom range
 * @param {Object} params.token - antd theme token: `similarityColormap` and the example colours
 * @param {Function} [params.onExampleMarked] - Called when the user has marked the example
 * @param {Function} [params.onError] - Called with the error when the example can't be made
 */
export const useSimilarityMap = ({
	mapObject,
	projectUuid,
	zoomRange,
	token,
	onExampleMarked,
	onError,
}) => {
	const [status, setStatus] = useState(SIMILARITY_STATUS.IDLE);
	const [featuresState, setFeaturesState] = useState(null);
	// The example on the map: its id and value range
	const [exemplar, setExemplar] = useState(null);
	const [threshold, setThresholdState] = useState(null);
	const [opacity, setOpacityState] = useState(DEFAULT_OPACITY);
	const [selectedId, setSelectedId] = useState(null);
	const [backbone, setBackboneState] = useState(DEFAULT_BACKBONE);
	// While marking: too far out for the hover preview's tile budget
	const [hoverZoomIn, setHoverZoomIn] = useState(false);

	const exampleLayerRef = useRef(null);
	const drawingRef = useRef(null);
	const heatLayerRef = useRef(null);
	const suggestionsLayerRef = useRef(null);
	// Read by the layers, which outlive a render
	const thresholdRef = useRef(null);
	const opacityRef = useRef(DEFAULT_OPACITY);
	const selectedIdRef = useRef(null);
	const patchMRef = useRef(DEFAULT_PATCH_M);
	const backboneRef = useRef(DEFAULT_BACKBONE);
	// The backbones whose features exist for this orthophoto
	const readyRef = useRef([]);
	// What marking another example returns to when cancelled
	const statusBeforeMarkingRef = useRef(SIMILARITY_STATUS.IDLE);
	// The objects marked so far, each with its examples and name ("car": a car and a van; "house"),
	// and the one examples are being added to. The heatmap compares with that one.
	const groupsRef = useRef([]);
	const activeRef = useRef(0);
	const [groups, setGroupsState] = useState([]);
	const [activeGroup, setActiveGroupState] = useState(0);
	const setGroups = useCallback(next => {
		groupsRef.current = next;
		setGroupsState(next);
		exampleLayerRef.current?.changed();
	}, []);
	const setActive = useCallback(index => {
		activeRef.current = index;
		setActiveGroupState(index);
	}, []);
	// The active object's example boxes, for the heatmap: "find all like these" sends every object's
	const boxesRef = useRef([]);
	const [exampleBoxes, setExampleBoxes] = useState([]);
	// The heatmap compares the whole orthophoto with the examples, and marks its strongest spots; off
	// until asked for
	const [heatmapVisible, setHeatmapVisibleState] = useState(false);
	const heatmapVisibleRef = useRef(false);
	// Bumped on every new example or close, so a late answer for an older one is dropped
	const requestRef = useRef(0);
	const callbacksRef = useRef({ onExampleMarked, onError });
	callbacksRef.current = { onExampleMarked, onError };

	const colors = token.similarityColormap;

	const { mutateAsync: createExemplar } = useMutation({
		mutationFn: similarityService.createExemplar,
	});

	const backbonesQuery = useQuery({
		queryKey: ['similarity', 'backbones', projectUuid],
		queryFn: () => similarityService.getBackbones({ projectUuid }),
		enabled: Boolean(projectUuid && similarityService.getBackbones),
		staleTime: 30000,
	});

	// The view suggestions are counted in (whole map units), set when the map settles
	const [suggestionView, setSuggestionView] = useState(null);
	useEffect(() => {
		if (!mapObject || !exemplar) return undefined;
		let timer;
		const update = () => {
			clearTimeout(timer);
			timer = setTimeout(() => {
				const extent = mapObject.getView().calculateExtent(mapObject.getSize());
				setSuggestionView(extent.map(Math.round));
			}, SUGGESTION_VIEW_DEBOUNCE_MS);
		};
		update();
		mapObject.on('moveend', update);
		return () => {
			clearTimeout(timer);
			mapObject.un('moveend', update);
		};
	}, [mapObject, exemplar]);

	// The view's spots, fetched again when the view moves, and only while the heatmap is shown
	const suggestionsQuery = useQuery({
		queryKey: [
			'similarity',
			'suggestions',
			projectUuid,
			exemplar?.id,
			exemplar?.backbone,
			exemplar?.valueRange,
			suggestionView,
		],
		queryFn: () =>
			similarityService.getSuggestions({
				projectUuid,
				exemplarId: exemplar.id,
				backbone: exemplar.backbone,
				minSimilarity: spotSimilarity(exemplar.valueRange),
				bounds: suggestionView,
			}),
		enabled: Boolean(exemplar && suggestionView && heatmapVisible),
		staleTime: Infinity,
		placeholderData: keepPreviousData,
		select: parseSuggestions,
	});
	const suggestions = suggestionsQuery.data ?? NO_SUGGESTIONS;

	const stopMarking = useCallback(() => {
		const drawing = drawingRef.current;
		if (!drawing) return;
		drawingRef.current = null;
		mapObject?.un('click', drawing.handleClick);
		drawing.stopHover?.();
		// After OpenLayers has finished handling the event that ended the drawing
		setTimeout(() => mapObject?.removeInteraction(drawing.draw), 0);
	}, [mapObject]);

	// Removes everything from the map and starts over
	const close = useCallback(() => {
		requestRef.current += 1;
		stopMarking();
		mapObject?.removeLayer(exampleLayerRef.current);
		exampleLayerRef.current = null;
		boxesRef.current = [];
		setExampleBoxes([]);
		groupsRef.current = [];
		setGroupsState([]);
		activeRef.current = 0;
		setActiveGroupState(0);
		thresholdRef.current = null;
		selectedIdRef.current = null;
		setStatus(SIMILARITY_STATUS.IDLE);
		setFeaturesState(null);
		setExemplar(null);
		setThresholdState(null);
		setSelectedId(null);
		heatmapVisibleRef.current = false;
		setHeatmapVisibleState(false);
	}, [mapObject, stopMarking]);

	const submitBoxes = useCallback(
		async boxes => {
			requestRef.current += 1;
			const request = requestRef.current;
			const requestBackbone = backboneFor(boxes, readyRef.current);
			backboneRef.current = requestBackbone;
			setBackboneState(requestBackbone);
			boxesRef.current = boxes;
			setExampleBoxes(boxes);
			const box = boxes[boxes.length - 1];
			setStatus(SIMILARITY_STATUS.LOADING);
			try {
				const result = await createExemplar({
					projectUuid,
					boxes,
					backbone: requestBackbone,
					// The mock puts its "orthophoto" in the view
					extent: mapObject.getView().calculateExtent(mapObject.getSize()),
				});
				if (request !== requestRef.current) return;
				if (result.patch_m) patchMRef.current = result.patch_m;
				if (result.status === 'features_missing') {
					setExemplar(null);
					setFeaturesState(result.features_state);
					setStatus(SIMILARITY_STATUS.UNAVAILABLE);
					return;
				}
				const valueRange = result.value_range ?? { lo: 0, hi: 1 };
				thresholdRef.current = defaultThreshold(valueRange);
				setThresholdState(thresholdRef.current);
				setExemplar({
					id: result.exemplar_id,
					valueRange,
					box,
					boxes,
					backbone: requestBackbone,
				});
				setStatus(SIMILARITY_STATUS.READY);
			} catch (error) {
				if (request !== requestRef.current) return;
				close();
				callbacksRef.current.onError?.(error);
			}
		},
		[createExemplar, mapObject, projectUuid, close]
	);

	// Marking: drag a box, or click the object's centre.
	// - 'new': start over with one object;
	// - 'add': another example of the active object, so objects of several sizes count as alike
	//   ("this car, and this van");
	// - 'object': the first example of a new object ("and houses"), searched with its own examples;
	// - a number: another example of that object.
	const start = useCallback(
		(mode = 'new') => {
			if (!mapObject) return;
			const fresh = mode === 'new' || !exampleLayerRef.current || !groupsRef.current.length;
			if (fresh) {
				close();
				groupsRef.current = [{ boxes: [], name: '' }];
				activeRef.current = 0;
			} else {
				stopMarking();
				if (mode === 'object' && groupsRef.current.length < MAX_OBJECTS) {
					groupsRef.current = [...groupsRef.current, { boxes: [], name: '' }];
					activeRef.current = groupsRef.current.length - 1;
				} else if (typeof mode === 'number' && groupsRef.current[mode]) {
					activeRef.current = mode;
				}
			}
			setGroups(groupsRef.current);
			setActive(activeRef.current);
			const group = activeRef.current;
			const layer =
				exampleLayerRef.current ??
				new VectorLayer({
					source: new VectorSource(),
					style: exampleStyle(token, groupsRef),
					zIndex: SIMILARITY_Z_INDEX + 2,
				});
			if (!exampleLayerRef.current) mapObject.addLayer(layer);
			exampleLayerRef.current = layer;
			const source = layer.getSource();

			const send = drawn => {
				// A small box, or a click, becomes one feature patch centred on the object
				const { box } = exampleBox(drawn, patchMRef.current);
				const feature = new Feature(fromExtent(box));
				feature.set('group', group);
				source.addFeature(feature);
				stopMarking();
				callbacksRef.current.onExampleMarked?.();
				const boxes = [...groupsRef.current[group].boxes, box];
				setGroups(groupsRef.current.map((g, i) => (i === group ? { ...g, boxes } : g)));
				submitBoxes(boxes);
			};
			// Dragging draws a box, as when annotating objects (object.draw.js)
			const draw = new Draw({
				type: 'Circle',
				geometryFunction: createBox(),
				freehand: true,
				style: exampleStyle(token, groupsRef),
			});
			draw.on('drawend', event => send(event.feature.getGeometry().getExtent()));
			const handleClick = event => send(boundingExtent([event.coordinate]));
			mapObject.addInteraction(draw);
			mapObject.on('click', handleClick);
			const drawing = { draw, handleClick };
			drawingRef.current = drawing;
			setStatus(current => {
				statusBeforeMarkingRef.current = current;
				return SIMILARITY_STATUS.MARKING;
			});

			// Hover preview (proof of concept): the similarity map follows the cursor while marking.
			// Without features (or with the mock) there is simply no preview.
			const hoverBackbone = backboneRef.current;
			similarityService
				.getFeatureGrid?.({ projectUuid, backbone: hoverBackbone })
				.then(grid => {
					if (drawingRef.current !== drawing) return;
					const hover = createSimilarityHover({
						projectUuid,
						backbone: hoverBackbone,
						grid,
						colors,
						opacity: opacityRef.current,
						zIndex: SIMILARITY_Z_INDEX,
						viewSize: mapObject.getSize(),
					});
					mapObject.addLayer(hover.layer);
					const view = mapObject.getView();
					// One update per animation frame, for the latest cursor position
					let frame = null;
					let coordinate = null;
					const onMove = event => {
						coordinate = event.coordinate;
						frame ??= requestAnimationFrame(() => {
							frame = null;
							hover.showAt(coordinate, view.calculateExtent(mapObject.getSize()));
						});
					};
					const updateZoomIn = () =>
						setHoverZoomIn(view.getResolution() >= hover.maxResolution);
					updateZoomIn();
					view.on('change:resolution', updateZoomIn);
					const viewport = mapObject.getViewport();
					mapObject.on('pointermove', onMove);
					viewport.addEventListener('pointerleave', hover.hide);
					drawing.stopHover = () => {
						cancelAnimationFrame(frame);
						view.un('change:resolution', updateZoomIn);
						setHoverZoomIn(false);
						mapObject.un('pointermove', onMove);
						viewport.removeEventListener('pointerleave', hover.hide);
						mapObject.removeLayer(hover.layer);
						hover.layer.dispose();
					};
				})
				.catch(() => {});
		},
		[
			mapObject,
			projectUuid,
			colors,
			token,
			close,
			stopMarking,
			submitBoxes,
			setGroups,
			setActive,
		]
	);

	// Name an object (the label of its results)
	const setGroupName = useCallback(
		(index, name) =>
			setGroups(groupsRef.current.map((g, i) => (i === index ? { ...g, name } : g))),
		[setGroups]
	);

	// The similarity overlay, for as long as there is an example
	useEffect(() => {
		if (!mapObject || !exemplar) return undefined;
		const layer = createSimilarityLayer({
			projectUuid,
			exemplarId: exemplar.id,
			backbone: exemplar.backbone,
			zoomRange,
			style: {
				colors,
				threshold: similarityToBand(thresholdRef.current, exemplar.valueRange),
				opacity: opacityRef.current,
			},
		});
		layer.setVisible(heatmapVisibleRef.current);
		mapObject.addLayer(layer);
		heatLayerRef.current = layer;
		return () => {
			mapObject.removeLayer(layer);
			layer.dispose();
		};
	}, [mapObject, exemplar, projectUuid, zoomRange, colors]);

	// The heatmap's strongest spots, as small dots on top of it. Clicking one outlines the object
	// (Find panel), from the box of the example's size the spot carries.
	useEffect(() => {
		if (!mapObject || !exemplar || suggestions.length === 0) return undefined;
		const layer = new VectorLayer({
			source: new VectorSource({
				features: suggestions.map(suggestion => {
					const feature = new Feature(new Point(suggestion.coordinate));
					feature.setId(suggestion.id);
					feature.set('bbox', suggestion.bbox);
					return feature;
				}),
			}),
			style: feature => spotStyle(token, feature.getId() === selectedIdRef.current),
			zIndex: SIMILARITY_Z_INDEX + 1,
		});
		layer.setVisible(heatmapVisibleRef.current);
		mapObject.addLayer(layer);
		suggestionsLayerRef.current = layer;
		return () => mapObject.removeLayer(layer);
	}, [mapObject, exemplar, suggestions, token]);

	const setHeatmapVisible = useCallback(visible => {
		heatmapVisibleRef.current = visible;
		heatLayerRef.current?.setVisible(visible);
		suggestionsLayerRef.current?.setVisible(visible);
		setHeatmapVisibleState(visible);
	}, []);

	// The backbones with features for this orthophoto. Before an example is marked (the hover preview
	// while marking), DINOv3 SAT-L where it exists; after, the example's size decides (backboneFor).
	useEffect(() => {
		readyRef.current = (backbonesQuery.data ?? [])
			.filter(({ state }) => state === 'ready')
			.map(({ key }) => key);
		if (boxesRef.current.length) return;
		if (readyRef.current.includes(PREFERRED_BACKBONE)) {
			backboneRef.current = PREFERRED_BACKBONE;
			setBackboneState(PREFERRED_BACKBONE);
		}
	}, [backbonesQuery.data]);

	const setOpacity = useCallback(value => {
		opacityRef.current = value;
		heatLayerRef.current?.updateStyleVariables({ opacity: value });
		setOpacityState(value);
	}, []);

	// Selects a suggestion and zooms to it, between the Layers sidebar and the panel
	const zoomToSuggestion = useCallback(
		suggestion => {
			selectedIdRef.current = suggestion.id;
			suggestionsLayerRef.current?.changed();
			setSelectedId(suggestion.id);
			mapObject?.getView().fit(suggestion.bbox ?? boundingExtent([suggestion.coordinate]), {
				padding: [120, 380, 120, 380],
				maxZoom: zoomRange.max,
				duration: 400,
			});
		},
		[mapObject, zoomRange.max]
	);

	// Cancel marking: back to the examples marked before, or to nothing
	// A new object without an example is dropped again; with nothing left, everything is
	const cancelMarking = useCallback(() => {
		const kept = groupsRef.current.filter(g => g.boxes.length);
		if (!kept.length) {
			close();
			return;
		}
		stopMarking();
		if (kept.length !== groupsRef.current.length) {
			setGroups(kept);
			setActive(kept.length - 1);
		}
		setStatus(statusBeforeMarkingRef.current);
	}, [close, stopMarking, setGroups, setActive]);

	// Clean up when leaving the project or the map
	useEffect(() => close, [close, projectUuid]);

	return {
		status,
		exampleBox: exampleBoxes[exampleBoxes.length - 1] ?? null,
		exampleBoxes,
		// Every object with its examples and name, and the one being added to (and in the heatmap)
		groups,
		activeGroup,
		setGroupName,
		heatmapVisible,
		setHeatmapVisible,
		// Another example of an object (the active one by default), or the first of a new object
		addExample: (group = activeRef.current) => start(group),
		addObject: () => start('object'),
		featuresState,
		valueRange: exemplar?.valueRange ?? null,
		colors,
		threshold,
		opacity,
		// The spots in the view, while the heatmap is shown
		spots: heatmapVisible ? suggestions : NO_SUGGESTIONS,
		spotsStatus: suggestionsQuery.status,
		selectedId,
		backbone,
		backbones: backbonesQuery.data ?? NO_BACKBONES,
		hoverZoomIn,
		start,
		cancelMarking,
		close,
		setOpacity,
		zoomToSuggestion,
	};
};
